// src/pages/VertragsImport.jsx
// Einmaliger Import der unterschriebenen Wartungsvertraege (Anna-Lena, 17.09.2026).
//
// Die Vertragsdaten wurden ausserhalb der App aus den PDFs gelesen und liegen
// zusammen mit den PDFs im Ordner "vertragsimport" auf dem Rechner des Bueros:
//   vertragsimport/import.json   (eine Zeile je Vertrag)
//   vertragsimport/pdf/*.pdf
// Der Ordner wird hier ausgewaehlt und NUR im Browser gelesen - Preise und
// Vertraege gehoeren nicht in den oeffentlichen Programmcode.
//
// Je Vertrag: Kunde (ueber Kundennummer) und Station bestaetigen oder neu
// anlegen, dann legt die App den Vertrag an und laedt das PDF hoch. Erledigte
// Zeilen merkt sich das Geraet, damit nichts doppelt importiert wird.

import { useState, useEffect, useMemo } from 'react';
import { supabase, withTimeout } from '../lib/supabase';
import { C, zurueckStil } from './formui';
import { VERTRAGSARTEN, LEISTUNGEN, vertragSpeichern, vertragPdfHochladen } from '../lib/vertraege';

const ERLEDIGT = 'etb-vertragsimport-erledigt';
const erledigtLesen = () => { try { return JSON.parse(localStorage.getItem(ERLEDIGT) || '{}') || {}; } catch { return {}; } };
const erledigtMerken = (datei, info) => {
  try { const e = erledigtLesen(); e[datei] = { ...info, zeit: Date.now() }; localStorage.setItem(ERLEDIGT, JSON.stringify(e)); } catch { /* egal */ }
};

const eingabe = { minHeight: 40, padding: '6px 10px', borderRadius: 8, border: '1.5px solid ' + C.gray200, fontSize: 15, background: C.white, color: C.navy, fontFamily: 'inherit', boxSizing: 'border-box' };
const knopf = (farbe = C.navy) => ({ minHeight: 40, padding: '7px 14px', borderRadius: 8, border: 'none', background: farbe, color: C.white, fontWeight: 800, fontSize: 14, cursor: 'pointer' });
const euro = (n) => (n == null ? '–' : Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €');
const datum = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('.') : '–');

function Zeile({ v, pdfDatei, erledigt, onErledigt }) {
  const [kundenTreffer, setKundenTreffer] = useState([]);
  const [kundeId, setKundeId] = useState('');
  const [kundenSuche, setKundenSuche] = useState(v.kunde?.kunden_nr || v.kunde?.name || '');
  const [stationen, setStationen] = useState([]);
  const [stationId, setStationId] = useState('neu');
  const [stationName, setStationName] = useState(v.station?.name || v.station?.standort_name || v.kunde?.name || '');
  const [vorhandenerVertrag, setVorhandenerVertrag] = useState(null);
  const [offen, setOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState(null);

  const art = v.vertrag?.art || 'wartung';

  // Kunde suchen: erst Kundennummer genau, sonst Name
  const kundenSuchen = async (text) => {
    const q = String(text || '').trim();
    if (!q) return;
    try {
      let { data } = await withTimeout(supabase.from('kunden').select('id, name, kunden_nr, ort').eq('kunden_nr', q).limit(5), 15000, 'Kunde suchen');
      if (!data || data.length === 0) {
        const wort = q.split(/\s+/).filter(w => w.length > 2).slice(0, 2).join('%');
        ({ data } = await withTimeout(supabase.from('kunden').select('id, name, kunden_nr, ort').ilike('name', '%' + (wort || q) + '%').limit(10), 15000, 'Kunde suchen'));
      }
      setKundenTreffer(data || []);
      if (data && data.length > 0 && !kundeId) setKundeId(data[0].id);
    } catch (e) {
      setMeldung('Kundensuche fehlgeschlagen: ' + (e.message || e));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!erledigt) kundenSuchen(v.kunde?.kunden_nr || v.kunde?.name); }, []);

  // Stationen des Kunden
  useEffect(() => {
    setStationen([]); setStationId('neu');
    if (!kundeId) return;
    (async () => {
      try {
        const { data } = await withTimeout(supabase.from('stationen')
          .select('id, bezeichnung, stations_nr, vnb_stationsbezeichnung, standort_strasse, standort_ort, objekt_strasse, objekt_ort')
          .eq('kunde_id', kundeId).eq('active', true).order('bezeichnung'), 15000, 'Stationen laden');
        const liste = data || [];
        setStationen(liste);
        // Vorschlag: VNB-Nummer, Name oder Strasse passen
        const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9äöüß]/g, '');
        const nr = norm(v.station?.vnb_stationsnummer);
        const name = norm(v.station?.name);
        const strasse = norm(v.station?.strasse).slice(0, 10);
        const passt = liste.find(s => nr && [s.stations_nr, s.vnb_stationsbezeichnung, s.bezeichnung].some(x => norm(x).includes(nr)))
          || liste.find(s => name && name.length > 3 && (norm(s.bezeichnung).includes(name) || name.includes(norm(s.bezeichnung))))
          || liste.find(s => strasse && strasse.length > 5 && [s.standort_strasse, s.objekt_strasse].some(x => norm(x).startsWith(strasse)))
          || (liste.length === 1 ? liste[0] : null);
        if (passt) setStationId(passt.id);
      } catch { /* bleibt "neu" */ }
    })();
  }, [kundeId, v.station?.vnb_stationsnummer, v.station?.name, v.station?.strasse]);

  // Gibt es an der Station schon einen Vertrag dieser Art?
  useEffect(() => {
    setVorhandenerVertrag(null);
    if (!stationId || stationId === 'neu') return;
    supabase.from('station_vertraege').select('id, pdf_pfad, beginn').eq('station_id', stationId).eq('art', art).maybeSingle()
      .then(({ data }) => setVorhandenerVertrag(data || null), () => {});
  }, [stationId, art]);

  const kundeAnlegen = async () => {
    if (!window.confirm('Kunden „' + (v.kunde?.name || '') + '“ neu anlegen?')) return;
    try {
      const { data, error } = await withTimeout(supabase.from('kunden').insert({
        name: v.kunde?.name, kunden_nr: v.kunde?.kunden_nr || null,
        strasse: v.kunde?.strasse || null, plz: v.kunde?.plz || null, ort: v.kunde?.ort || null, active: true,
      }).select('id, name, kunden_nr, ort').single(), 15000, 'Kunde anlegen');
      if (error) throw error;
      setKundenTreffer([data]); setKundeId(data.id);
    } catch (e) {
      setMeldung('Kunde nicht angelegt: ' + (e.message || e));
    }
  };

  const importieren = async () => {
    if (laeuft) return;
    if (!kundeId) { setMeldung('Bitte zuerst den Kunden auswählen.'); return; }
    if (v.ueberspringen && !window.confirm('Dieser Vertrag ist als „nicht importieren“ markiert:\n\n' + v.import_hinweis + '\n\nTrotzdem importieren?')) return;
    if (vorhandenerVertrag && !window.confirm('Diese Station hat schon einen ' + (VERTRAGSARTEN.find(a => a.art === art)?.text || 'Vertrag')
      + '. Die Angaben aus diesem Vertrag darüber schreiben?')) return;
    setLaeuft(true); setMeldung(null);
    try {
      const { data: sitzung } = await supabase.auth.getSession();
      let sid = stationId;
      if (sid === 'neu') {
        const s = v.station || {};
        const k = v.kunde || {};
        const abweichend = !!(s.strasse && k.strasse && s.strasse.trim().toLowerCase() !== k.strasse.trim().toLowerCase());
        const { data, error } = await withTimeout(supabase.from('stationen').insert({
          kunde_id: kundeId,
          bezeichnung: stationName.trim() || s.name || k.name || 'Station',
          vnb_stationsbezeichnung: s.vnb_stationsnummer || null,
          standort_strasse: s.strasse || k.strasse || null, standort_plz: s.plz || k.plz || null, standort_ort: s.ort || k.ort || null,
          objekt_abweichend: abweichend,
          objekt_name: abweichend ? (s.standort_name || null) : null,
          objekt_strasse: abweichend ? s.strasse : null, objekt_plz: abweichend ? (s.plz || null) : null, objekt_ort: abweichend ? (s.ort || null) : null,
          objekt_ansprechpartner: v.ansprechpartner?.name || null, objekt_telefon: v.ansprechpartner?.telefon || null,
          objekt_mobil: v.ansprechpartner?.mobil || null, objekt_email: v.ansprechpartner?.email || null,
          notizen: v.anlage?.beschreibung ? 'Aus Vertrag: ' + v.anlage.beschreibung : null,
          active: true, created_by: sitzung?.session?.user?.id || null,
        }).select('id').single(), 20000, 'Station anlegen');
        if (error) throw error;
        sid = data.id;
      }
      const vt = v.vertrag || {};
      const felder = {
        vorhanden: true,
        beginn: vt.beginn, laufzeit_jahre: vt.laufzeit_jahre,
        wartungszyklus_jahre: vt.wartungszyklus_jahre, sichtpruefung_jahre: vt.sichtpruefung_jahre,
        entgelt_wartung: vt.entgelt_wartung, entgelt_sichtpruefung: vt.entgelt_sichtpruefung,
        zahlungsziel: vt.zahlungsziel, preisbindung_jahre: vt.preisbindung_jahre,
        erste_wartung: vt.erste_wartung, erste_sichtpruefung: vt.erste_sichtpruefung,
        leistungen: vt.leistungen || {},
        freigabe_schriftlich: !!vt.freigabe_schriftlich, kunde_informiert_vnb: !!vt.kunde_informiert_vnb,
        kunde_stellt_schaltberechtigten: !!vt.kunde_stellt_schaltberechtigten,
        besondere_vereinbarungen: vt.besondere_vereinbarungen,
        notiz: [vt.notiz, 'Importiert aus ' + v.datei].filter(Boolean).join(' · '),
      };
      if (vorhandenerVertrag?.id) felder.id = vorhandenerVertrag.id;
      await vertragSpeichern(sid, art, felder);
      if (pdfDatei) {
        const pdf = await vertragPdfHochladen(sid, art, pdfDatei);
        await vertragSpeichern(sid, art, { ...pdf, vorhanden: true });
      }
      erledigtMerken(v.datei, { station_id: sid });
      onErledigt();
    } catch (e) {
      setMeldung('Import fehlgeschlagen: ' + (e.message || e));
    } finally {
      setLaeuft(false);
    }
  };

  const vt = v.vertrag || {};
  const leistungen = LEISTUNGEN.filter(l => vt.leistungen?.[l.key]).map(l => l.text);
  const unsicher = v.unsicher || [];

  return (
    <div style={{ background: C.white, borderRadius: 10, padding: '12px 14px', marginBottom: 10,
                  borderLeft: '4px solid ' + (erledigt ? C.green : unsicher.length ? C.orange : C.navy), opacity: erledigt ? 0.75 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontWeight: 800, color: C.navy, fontSize: 16 }}>
            {v.kunde?.name || '–'} {v.kunde?.kunden_nr && <span style={{ fontSize: 13, color: C.gray500 }}>Nr. {v.kunde.kunden_nr}</span>}
          </div>
          <div style={{ fontSize: 14, color: C.gray700 }}>
            {[v.station?.name, v.station?.vnb_stationsnummer, [v.station?.strasse, [v.station?.plz, v.station?.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
          </div>
          <div style={{ fontSize: 13, color: C.gray500 }}>{v.datei}{!pdfDatei && <span style={{ color: C.red, fontWeight: 700 }}> · PDF nicht gefunden</span>}</div>
        </div>
        <div style={{ fontSize: 14, color: C.navy, textAlign: 'right' }}>
          <div><strong>{VERTRAGSARTEN.find(a => a.art === art)?.text}</strong>{v.anlagentyp && v.anlagentyp !== 'trafostation' ? ' · ' + v.anlagentyp : ''}</div>
          <div>Beginn {datum(vt.beginn)} · {vt.laufzeit_jahre ? vt.laufzeit_jahre + ' J. Laufzeit' : 'Laufzeit –'}</div>
          <div>Zyklus {vt.wartungszyklus_jahre ? vt.wartungszyklus_jahre + ' J.' : '–'} · Sichtprüfung {vt.sichtpruefung_jahre ? vt.sichtpruefung_jahre + ' J.' : '–'}</div>
          <div>{euro(vt.entgelt_wartung)} je Wartung{vt.entgelt_sichtpruefung != null ? ' · ' + euro(vt.entgelt_sichtpruefung) + ' Sichtprüfung' : ''}</div>
        </div>
      </div>

      {v.import_hinweis && !erledigt && (
        <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 7, fontSize: 14, fontWeight: 700,
                      background: v.ueberspringen ? C.redBg : C.orangeBg, color: v.ueberspringen ? C.red : C.orangeText }}>
          {v.ueberspringen ? '⛔ ' : '⚠ '}{v.import_hinweis}
        </div>
      )}
      {erledigt ? (
        <div style={{ marginTop: 8, fontWeight: 800, color: C.green }}>✓ importiert</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
            <strong style={{ fontSize: 14, color: C.navy, minWidth: 60 }}>Kunde</strong>
            <input value={kundenSuche} onChange={e => setKundenSuche(e.target.value)} style={{ ...eingabe, width: 180 }} placeholder="Nr. oder Name"/>
            <button type="button" onClick={() => kundenSuchen(kundenSuche)} style={knopf(C.gray700)}>Suchen</button>
            <select value={kundeId} onChange={e => setKundeId(e.target.value)} style={{ ...eingabe, flex: '1 1 240px' }}>
              <option value="">– Kunde wählen –</option>
              {kundenTreffer.map(k => <option key={k.id} value={k.id}>{k.name} {k.kunden_nr ? '(' + k.kunden_nr + ')' : ''} {k.ort || ''}</option>)}
            </select>
            {kundenTreffer.length === 0 && <button type="button" onClick={kundeAnlegen} style={knopf(C.orange)}>Kunde neu anlegen</button>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
            <strong style={{ fontSize: 14, color: C.navy, minWidth: 60 }}>Station</strong>
            <select value={stationId} onChange={e => setStationId(e.target.value)} style={{ ...eingabe, flex: '1 1 240px' }} disabled={!kundeId}>
              <option value="neu">+ neue Station anlegen</option>
              {stationen.map(s => <option key={s.id} value={s.id}>{s.bezeichnung}{s.stations_nr ? ' · ' + s.stations_nr : ''}{s.standort_ort || s.objekt_ort ? ' · ' + (s.objekt_ort || s.standort_ort) : ''}</option>)}
            </select>
            {stationId === 'neu' && (
              <input value={stationName} onChange={e => setStationName(e.target.value)} style={{ ...eingabe, flex: '1 1 200px' }} placeholder="Name der neuen Station"/>
            )}
          </div>
          {vorhandenerVertrag && (
            <div style={{ marginTop: 6, fontSize: 14, color: C.orangeText, fontWeight: 700 }}>
              Achtung: An dieser Station gibt es schon diesen Vertrag{vorhandenerVertrag.pdf_pfad ? ' (mit PDF)' : ''} – der Import schreibt darüber.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
            <button type="button" onClick={importieren} disabled={laeuft || !kundeId} style={knopf(laeuft || !kundeId ? C.gray300 : C.green)}>
              {laeuft ? 'importiert …' : v.ueberspringen ? 'trotzdem importieren' : 'Prüfen OK – importieren'}
            </button>
            <button type="button" onClick={() => setOffen(!offen)} style={{ ...knopf(C.white), color: C.navy, border: '1.5px solid ' + C.gray200 }}>
              {offen ? 'Details ausblenden' : 'Details' + (unsicher.length ? ' (' + unsicher.length + ' unsicher)' : '')}
            </button>
            {meldung && <span style={{ color: C.red, fontWeight: 700, fontSize: 14 }}>{meldung}</span>}
          </div>
          {offen && (
            <div style={{ marginTop: 10, fontSize: 14, color: C.navy, background: C.gray100, borderRadius: 8, padding: 10, lineHeight: 1.5 }}>
              <div><strong>Anlage:</strong> {v.anlage?.beschreibung || '–'}</div>
              <div><strong>Leistungen:</strong> {leistungen.join(', ') || '–'}{vt.leistungen?.oelprobe ? ' · Ölprobe: ' + vt.leistungen.oelprobe : ''}</div>
              <div><strong>Erste Termine:</strong> Wartung {datum(vt.erste_wartung)} · Sichtprüfung {datum(vt.erste_sichtpruefung)}</div>
              <div><strong>Zahlungsziel:</strong> {vt.zahlungsziel || '–'} · <strong>Preisbindung:</strong> {vt.preisbindung_jahre ? vt.preisbindung_jahre + ' J.' : '–'}</div>
              <div><strong>Absprachen:</strong> {[vt.freigabe_schriftlich && 'schriftliche Freigabe', vt.kunde_informiert_vnb && 'Kunde informiert VNB', vt.kunde_stellt_schaltberechtigten && 'Kunde stellt Schaltberechtigten'].filter(Boolean).join(', ') || '–'}</div>
              <div><strong>Besondere Vereinbarungen:</strong> {vt.besondere_vereinbarungen || '–'}</div>
              <div><strong>Ansprechpartner:</strong> {[v.ansprechpartner?.name, v.ansprechpartner?.telefon, v.ansprechpartner?.email].filter(Boolean).join(' · ') || '–'}</div>
              {unsicher.length > 0 && (
                <div style={{ marginTop: 6, color: C.orangeText }}>
                  <strong>Unsicher – bitte im PDF prüfen:</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>{unsicher.map((u, i) => <li key={i}>{u}</li>)}</ul>
                </div>
              )}
              <div style={{ marginTop: 6, color: C.gray500 }}>Nach dem Import lassen sich alle Angaben in der Station unter „Verträge“ korrigieren.</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function VertragsImport({ onBack }) {
  const [vertraege, setVertraege] = useState(null);
  const [pdfs, setPdfs] = useState({});
  const [fehler, setFehler] = useState(null);
  const [erledigt, setErledigt] = useState(erledigtLesen);
  const [filter, setFilter] = useState('offen');

  const ordnerGewaehlt = async (e) => {
    setFehler(null);
    const dateien = Array.from(e.target.files || []);
    const json = dateien.find(f => f.name === 'import.json');
    if (!json) { setFehler('Im gewählten Ordner fehlt die Datei „import.json“. Bitte den Ordner „vertragsimport“ auswählen.'); return; }
    try {
      const daten = JSON.parse(await json.text());
      const nachName = {};
      dateien.filter(f => f.name.toLowerCase().endsWith('.pdf')).forEach(f => { nachName[f.name] = f; });
      setPdfs(nachName);
      setVertraege(Array.isArray(daten) ? daten : daten.vertraege || []);
    } catch (err) {
      setFehler('import.json konnte nicht gelesen werden: ' + (err.message || err));
    }
  };

  const liste = useMemo(() => (vertraege || []).filter(v => filter === 'alle' || (filter === 'offen' ? !erledigt[v.datei] : !!erledigt[v.datei])),
    [vertraege, erledigt, filter]);

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif" }}>
      <div style={{ background: C.navy, padding: '14px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onBack} style={zurueckStil}>‹ Zurück</button>
          <div>
            <div style={{ color: C.white, fontWeight: 800, fontSize: 18 }}>Verträge importieren</div>
            <div style={{ color: C.onDark, fontSize: 14 }}>Kunde und Station prüfen – die App legt Vertrag und PDF an</div>
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px 14px 40px' }}>
        {!vertraege && (
          <div style={{ background: C.white, borderRadius: 10, padding: 18, fontSize: 15, color: C.navy, lineHeight: 1.5 }}>
            <p style={{ marginTop: 0 }}>Bitte den Ordner <strong>„vertragsimport“</strong> auswählen (liegt im Projektordner der App auf dem PC). Die Dateien werden nur hier im Browser gelesen.</p>
            <label style={{ ...knopf(), display: 'inline-flex', alignItems: 'center' }}>
              Ordner auswählen
              <input type="file" webkitdirectory="" directory="" multiple onChange={ordnerGewaehlt} style={{ display: 'none' }}/>
            </label>
            {fehler && <div style={{ marginTop: 10, color: C.red, fontWeight: 700 }}>{fehler}</div>}
          </div>
        )}
        {vertraege && (
          <>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
              {[['offen', 'Noch offen'], ['erledigt', 'Importiert'], ['alle', 'Alle']].map(([k, t]) => (
                <button key={k} type="button" onClick={() => setFilter(k)}
                  style={{ ...knopf(filter === k ? C.navy : C.white), color: filter === k ? C.white : C.gray700, border: '1.5px solid ' + (filter === k ? C.navy : C.gray200) }}>{t}</button>
              ))}
              <span style={{ fontSize: 14, color: C.gray500, marginLeft: 8 }}>
                {Object.keys(erledigt).filter(d => vertraege.some(v => v.datei === d)).length} von {vertraege.length} importiert
              </span>
            </div>
            {liste.map(v => (
              <Zeile key={v.datei} v={v} pdfDatei={pdfs[v.datei] || null} erledigt={!!erledigt[v.datei]}
                onErledigt={() => setErledigt(erledigtLesen())}/>
            ))}
            {liste.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: C.gray500 }}>Nichts in dieser Ansicht.</div>}
          </>
        )}
      </div>
    </div>
  );
}
